import { logSentryException } from '@/common/util/sentry'
import Events from '@/configuration/Events'
import {
  getIsReadyToPayRequest,
  getGooglePaymentDataRequest
} from '@/ghost/utils/googlePaymentData'
import {
  errorTranslationMap,
  defaultError,
  noUserInteractionMessage
} from '@/configuration/sources/smartform/googlePay.yml'
export default class GooglePayManager {
  constructor({ $store, $bus, proxy, storeFactory }) {
    this.$store = $store
    this.$bus = $bus
    this.proxy = proxy
    this.storeFactory = storeFactory
  }

  static clientInstance = undefined

  static getClientInstance(testMode = true) {
    if (this.clientInstance) return this.clientInstance
    if (typeof window.google?.payments?.api?.PaymentsClient !== 'function')
      return undefined
    this.clientInstance = new google.payments.api.PaymentsClient({
      environment: testMode ? 'TEST' : 'PRODUCTION'
    })
    return this.clientInstance
  }

  static isReadyToPayCall({
    testMode,
    cvcRequired,
    dnaBrands,
    shippingAddressRequired,
    disableVisaOnWallet,
    cbEnabled
  }) {
    const client = GooglePayManager.getClientInstance(testMode)
    return client?.isReadyToPay(
      getIsReadyToPayRequest({
        cvcRequired,
        dnaBrands,
        shippingAddressRequired,
        disableVisaOnWallet,
        cbEnabled
      })
    )
  }

  /**
   *
   * @param {GooglePaymentParams} paymentParams
   */
  async start(paymentParams) {
    const { testMode } = paymentParams

    try {
      const result = await this.getGooglePayResult(paymentParams)

      // Google payment will be triggered from the GetPayloadInfos or the extra fields modal
      this.$bus.$emit(Events.krypton.message.getPayloadInfos, {
        payload: result,
        payloadType: 'GOOGLEPAY',
        testMode
      })
    } catch (googlePayError) {
      let error = this.getError(googlePayError)

      if (error.errorCode === 'CLIENT_518') {
        this.$store.dispatch('openConfirmationModal', {
          method: 'GOOGLEPAY',
          errorCode: error.errorCode
        })
      } else {
        this.$store.dispatch('error', error)
        logSentryException(googlePayError, 'ghost/service/GooglePayManager')
      }
    }
  }

  /**
   * @param {GooglePaymentParams} paymentParams
   */
  async getGooglePayResult(paymentParams) {
    const { testMode, testModeWithoutSimulator } = paymentParams
    return testMode && !testModeWithoutSimulator
      ? this.getResultFromSimulator()
      : this.getResult(paymentParams, testMode)
  }

  getResultFromSimulator() {
    // This call is made from the ghost and the simulator is in the host
    // Need communication via proxy.send
    const result = new Promise((resolve, reject) => {
      this.$bus.$on(
        Events.krypton.message.googlePaySimulatorAction,
        actionInfo => {
          if (actionInfo.type === 'close') {
            this.proxy.send(this.storeFactory.create('closeGooglePaySimulator'))

            // Only unsubscribe on close, as it can receive multiple events before closing
            this.$bus.$off(Events.krypton.message.googlePaySimulatorAction)
            const statusMessage = errorTranslationMap.find(
              ({ mappedErrorCode }) => mappedErrorCode === 'CLIENT_604'
            )?.errorMessage
            reject({ statusCode: 'CANCELED', statusMessage })
            return
          }
          resolve(actionInfo.payload)
        }
      )
    })

    this.$store.dispatch('openGooglePaySimulator')
    return result
  }

  async getResult(paymentParams, testMode) {
    const client = GooglePayManager.getClientInstance(testMode)
    const result = await client.loadPaymentData(
      getGooglePaymentDataRequest(paymentParams)
    )
    return result
  }

  getError(googlePayError) {
    let error = {
      paymentMethod: 'GOOGLEPAY'
    }

    const googlePayErrorMessage =
      googlePayError?.message || googlePayError?.statusMessage

    // Sometimes the SDK does not return a custom error message
    // If the error is a string containing 'failed to open window' throw the CLIENT_518 error
    if (googlePayError === noUserInteractionMessage)
      error.errorCode = 'CLIENT_518'
    // Throw a generic transaction aborted error as a fallback
    else if (!googlePayErrorMessage) error.errorCode = 'CLIENT_101'
    else {
      // Find error code on map or get the default gpay error message if nothing was found
      const errorCode =
        errorTranslationMap.find(({ errorMessage }) =>
          googlePayErrorMessage.includes(errorMessage)
        )?.mappedErrorCode ?? defaultError

      error.errorCode = errorCode
      error.metadata = { googlePayErrorMessage }
    }

    return error
  }
}
